/**
 * @module components/AmbientMode/AmbientModeOverlay
 * @description Overlay de pantalla completa del modo ambient con animaciones de fade.
 *
 * Se monta sobre todo el contenido de la aplicación cuando `isAmbient = true`.
 * Gestiona las transiciones de entrada y salida:
 * - **Fade-in**: al montarse, aplica transición CSS de opacidad 0→1
 * - **Fade-out**: cuando `fadingOut = true`, aplica transición 1→0 antes de desmontarse
 *
 * El `FADE_OUT_MS` en `App.tsx` debe sincronizarse con la duración CSS de la transición.
 * Usa `memo()` para evitar re-renders innecesarios durante la rotación de escenas.
 *
 * @see SceneEngine para el motor de rotación de escenas
 * @see AmbientModeContext para el estado isAmbient y los callbacks
 * @see App.tsx para el FADE_OUT_MS y la lógica de montaje/desmontaje
 */
import React, { memo, useEffect, useRef, useState } from 'react';
import { SceneEngine } from './SceneEngine';

interface AmbientModeOverlayProps {
  /** Cuando es true, aplica fade-out antes de desmontarse */
  fadingOut: boolean;
}

export const AmbientModeOverlay = memo(function AmbientModeOverlay({
  fadingOut,
}: AmbientModeOverlayProps) {
  const [mounted, setMounted] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Fade-in al montar
  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  return (
    <div
      ref={overlayRef}
      className={`
        fixed inset-0 z-[60]
        bg-gray-950 text-gray-100
        overflow-hidden select-none
        transition-opacity duration-500
        ${mounted && !fadingOut ? 'opacity-100' : 'opacity-0'}
      `}
      role="presentation"
      aria-label="Modo Monitoreo Ambient — mueve el mouse o presiona una tecla para continuar"
    >
      {/* Motor de escenas */}
      <SceneEngine overlayRef={overlayRef as React.RefObject<HTMLDivElement>} />

      {/* Indicador de activación en la esquina */}
      <div className="absolute top-4 right-6 flex items-center gap-2 pointer-events-none">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        <span className="text-[10px] text-gray-600 uppercase tracking-widest font-mono">
          Modo Monitoreo
        </span>
      </div>

      {/* Hint de activación */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none">
        <p className="text-[10px] text-gray-700 tracking-widest">
          Mueve el mouse o presiona cualquier tecla para continuar
        </p>
      </div>
    </div>
  );
});
